import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
    UsersIcon,
    DocumentDuplicateIcon,
    ChatBubbleLeftRightIcon,
    DocumentCheckIcon,
    ClockIcon,
    ArrowTrendingUpIcon,
    UserPlusIcon,
    ChartBarIcon
} from '@heroicons/react/24/outline';
import API_URL from '../config/apiConfig';

const AdminOverview = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem('auth-token');
            const response = await axios.get(`${API_URL}/admin/stats`, {
                headers: { 'auth-token': token }
            });
            if (response.data.success) {
                setStats(response.data.data);
            }
        } catch (error) {
            console.error('Erreur stats:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="w-8 h-8 border-4 border-[#Ceaa5b] border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    if (!stats) {
        return <div className="text-center py-10 text-gray-500">Impossible de charger les statistiques.</div>;
    }

    const quotaUsagePercent = stats.averageQuota > 0
        ? Math.round((stats.totalPDFs / (stats.totalUsers * stats.averageQuota)) * 100)
        : 0;

    return (
        <div className="space-y-8">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold text-gray-900">Tableau de bord</h1>
                <span className="text-sm text-gray-400">Mis à jour à l'instant</span>
            </div>

            {/* =================== CARTES PRINCIPALES =================== */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                <StatCard
                    title="Utilisateurs"
                    value={stats.totalUsers}
                    subtitle={`${stats.activeUsers} actifs`}
                    icon={UsersIcon}
                    color="bg-blue-500"
                />
                <StatCard
                    title="Rapports PDF"
                    value={stats.totalPDFs}
                    subtitle={`${stats.completedConversations} finalisés`}
                    icon={DocumentDuplicateIcon}
                    color="bg-purple-500"
                />
                <StatCard
                    title="Conversations"
                    value={stats.totalConversations}
                    subtitle={`${stats.activeConversations} en cours`}
                    icon={ChatBubbleLeftRightIcon}
                    color="bg-emerald-500"
                />
                <StatCard
                    title="Messages échangés"
                    value={stats.totalMessages}
                    subtitle={`Quota moyen : ${stats.averageQuota}`}
                    icon={ChartBarIcon}
                    color="bg-amber-500"
                />
            </div>

            {/* =================== ACTIVITÉ 7 JOURS =================== */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <ClockIcon className="w-5 h-5 text-gray-400" />
                    Activité des 7 derniers jours
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                    <ActivityItem
                        label="Nouvelles conversations"
                        value={stats.recentConversations}
                        icon={ChatBubbleLeftRightIcon}
                        color="text-emerald-600 bg-emerald-50"
                    />
                    <ActivityItem
                        label="Rapports générés"
                        value={stats.recentReports}
                        icon={DocumentCheckIcon}
                        color="text-purple-600 bg-purple-50"
                    />
                    <ActivityItem
                        label="Nouveaux utilisateurs"
                        value={stats.recentUsers}
                        icon={UserPlusIcon}
                        color="text-blue-600 bg-blue-50"
                    />
                </div>
            </div>

            {/* =================== 2 COLONNES : USERS + RAPPORTS =================== */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                {/* --- DERNIERS UTILISATEURS --- */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold text-gray-900">Derniers inscrits</h2>
                        <Link to="/admin/users" className="text-sm text-[#Ceaa5b] hover:underline font-medium">
                            Voir tout
                        </Link>
                    </div>
                    <div className="space-y-3">
                        {stats.latestUsers && stats.latestUsers.length > 0 ? (
                            stats.latestUsers.map(user => (
                                <div key={user.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                                    <div className="flex items-center gap-3">
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${user.isActive ? 'bg-emerald-500' : 'bg-gray-400'}`}>
                                            {(user.name || 'U').charAt(0).toUpperCase()}
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-gray-900">{user.name || 'Sans nom'}</p>
                                            <p className="text-xs text-gray-400">{user.email}</p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="flex items-center gap-2">
                                            {user.isAdmin && <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded font-medium">Admin</span>}
                                            {user.isDemo && <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded font-medium">Demo</span>}
                                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${user.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                                                {user.isActive ? 'Actif' : 'Inactif'}
                                            </span>
                                        </div>
                                        <p className="text-[10px] text-gray-400 mt-1">
                                            {new Date(user.createdAt).toLocaleDateString('fr-FR')}
                                        </p>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p className="text-sm text-gray-400 italic">Aucun utilisateur.</p>
                        )}
                    </div>
                </div>

                {/* --- DERNIERS RAPPORTS --- */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold text-gray-900">Derniers rapports</h2>
                        <span className="text-sm text-gray-400">{stats.completedConversations} au total</span>
                    </div>
                    <div className="space-y-3">
                        {stats.latestReports && stats.latestReports.length > 0 ? (
                            stats.latestReports.map(report => (
                                <div key={report.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-lg bg-[#Ceaa5b]/10 flex items-center justify-center">
                                            <DocumentCheckIcon className="w-4 h-4 text-[#Ceaa5b]" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-gray-900 truncate max-w-[200px]">
                                                {report.title || 'Rapport'}
                                            </p>
                                            <p className="text-xs text-gray-400">
                                                {report.user ? report.user.name || report.user.email : 'Utilisateur inconnu'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {report.pdfUrl && (
                                            <a
                                                href={report.pdfUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-xs text-[#Ceaa5b] hover:underline font-medium"
                                            >
                                                Ouvrir
                                            </a>
                                        )}
                                        <span className="text-[10px] text-gray-400">
                                            {new Date(report.updatedAt).toLocaleDateString('fr-FR')}
                                        </span>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p className="text-sm text-gray-400 italic">Aucun rapport finalisé.</p>
                        )}
                    </div>
                </div>
            </div>

            {/* =================== TOP UTILISATEURS =================== */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <ArrowTrendingUpIcon className="w-5 h-5 text-gray-400" />
                    Utilisateurs les plus actifs
                </h2>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs text-gray-400 uppercase tracking-wider border-b border-gray-100">
                                <th className="pb-3 font-medium">Utilisateur</th>
                                <th className="pb-3 font-medium">Email</th>
                                <th className="pb-3 font-medium text-center">PDF générés</th>
                                <th className="pb-3 font-medium text-center">Quota</th>
                                <th className="pb-3 font-medium">Utilisation</th>
                            </tr>
                        </thead>
                        <tbody>
                            {stats.topUsers && stats.topUsers.length > 0 ? (
                                stats.topUsers.map((user, index) => {
                                    const usage = user.pdfMonthlyQuota > 0
                                        ? Math.round(((user.pdfMonthlyCount || 0) / user.pdfMonthlyQuota) * 100)
                                        : 0;
                                    const barColor = usage > 80 ? 'bg-red-500' : usage > 50 ? 'bg-amber-500' : 'bg-emerald-500';

                                    return (
                                        <tr key={user.id} className="border-b border-gray-50 last:border-0">
                                            <td className="py-3">
                                                <div className="flex items-center gap-2">
                                                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold
                                                        ${index === 0 ? 'bg-[#Ceaa5b]' : index === 1 ? 'bg-gray-400' : 'bg-gray-300'}
                                                    `}>
                                                        {index + 1}
                                                    </span>
                                                    <span className="font-medium text-gray-900">{user.name || 'Sans nom'}</span>
                                                </div>
                                            </td>
                                            <td className="py-3 text-gray-500">{user.email}</td>
                                            <td className="py-3 text-center font-semibold text-gray-900">{user.pdfMonthlyCount || 0}</td>
                                            <td className="py-3 text-center text-gray-500">{user.pdfMonthlyQuota}</td>
                                            <td className="py-3">
                                                <div className="flex items-center gap-2">
                                                    <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                                                        <div
                                                            className={`h-full rounded-full transition-all ${barColor}`}
                                                            style={{ width: `${Math.min(usage, 100)}%` }}
                                                        ></div>
                                                    </div>
                                                    <span className="text-xs text-gray-500 w-10 text-right">{usage}%</span>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={5} className="py-4 text-center text-gray-400 italic">Aucune donnée.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* =================== ACTIONS RAPIDES =================== */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Link to="/admin/users" className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 hover:border-[#Ceaa5b]/40 hover:shadow-md transition-all group">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-50 rounded-lg group-hover:bg-blue-100 transition-colors">
                            <UsersIcon className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                            <h3 className="font-medium text-gray-900 text-sm">Gérer les utilisateurs</h3>
                            <p className="text-xs text-gray-400">Ajouter, modifier, supprimer</p>
                        </div>
                    </div>
                </Link>
                <Link to="/admin/quotas" className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 hover:border-[#Ceaa5b]/40 hover:shadow-md transition-all group">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-purple-50 rounded-lg group-hover:bg-purple-100 transition-colors">
                            <ChartBarIcon className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                            <h3 className="font-medium text-gray-900 text-sm">Gérer les quotas</h3>
                            <p className="text-xs text-gray-400">Ajuster les limites PDF</p>
                        </div>
                    </div>
                </Link>
                <Link to="/admin/stats" className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 hover:border-[#Ceaa5b]/40 hover:shadow-md transition-all group">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-emerald-50 rounded-lg group-hover:bg-emerald-100 transition-colors">
                            <ArrowTrendingUpIcon className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                            <h3 className="font-medium text-gray-900 text-sm">Statistiques détaillées</h3>
                            <p className="text-xs text-gray-400">Voir les tendances</p>
                        </div>
                    </div>
                </Link>
            </div>
        </div>
    );
};

/* =================== COMPOSANTS INTERNES =================== */

const StatCard = ({ title, value, subtitle, icon: Icon, color }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition-shadow">
        <div className="flex items-center justify-between">
            <div>
                <p className="text-sm text-gray-500 font-medium">{title}</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
                <p className="text-xs text-gray-400 mt-1">{subtitle}</p>
            </div>
            <div className={`p-3 rounded-xl ${color}`}>
                <Icon className="w-6 h-6 text-white" />
            </div>
        </div>
    </div>
);

const ActivityItem = ({ label, value, icon: Icon, color }) => (
    <div className="flex items-center gap-4 p-4 rounded-lg bg-gray-50">
        <div className={`p-2.5 rounded-lg ${color}`}>
            <Icon className="w-5 h-5" />
        </div>
        <div>
            <p className="text-2xl font-bold text-gray-900">{value}</p>
            <p className="text-xs text-gray-500">{label}</p>
        </div>
    </div>
);

export default AdminOverview;
